/**
 * Selection state machine: controlled-or-internal selection plus click
 * handlers (module / mover / empty-canvas). Hoisted out of XtsViewer3D so
 * the host component reads as a flat dependency-injection of props →
 * sub-features.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  EMPTY_SELECTION,
  applyEmptyClick,
  applyModuleClick,
  applyMoverClick,
} from '../../interaction/SelectionManager.js';
import type {
  ModuleRef,
  MoverRef,
  SelectionMode,
  SelectionState,
} from '../../types.js';

export interface UseSelectionStateProps {
  /** Controlled selection. When supplied, internal state is bypassed. */
  selection?: SelectionState;
  selectionMode?: SelectionMode;
  selectionLocked?: boolean;
  onSelectionChange?: (s: SelectionState) => void;
  onModuleClick?: (ref: ModuleRef) => void;
  onMoverClick?: (ref: MoverRef) => void;
}

export interface SelectionHandles {
  effectiveSelection: SelectionState;
  selectionMode: SelectionMode;
  handleModuleClick: (
    ref: ModuleRef,
    e: { nativeEvent?: MouseEvent },
  ) => void;
  handleMoverClick: (
    ref: MoverRef,
    e: { nativeEvent?: MouseEvent },
  ) => void;
  handlePointerMissed: () => void;
}

export function useSelectionState(
  props: UseSelectionStateProps,
): SelectionHandles {
  const [internal, setInternal] = useState<SelectionState>(EMPTY_SELECTION);
  const selectionMode: SelectionMode = props.selectionMode ?? 'Off';
  const isControlled = props.selection !== undefined;
  const effectiveSelection = isControlled
    ? (props.selection as SelectionState)
    : internal;

  const updateSelection = useCallback(
    (next: SelectionState) => {
      if (!isControlled) setInternal(next);
      props.onSelectionChange?.(next);
    },
    [isControlled, props.onSelectionChange],
  );

  const handleModuleClick = useMemo(
    () => (ref: ModuleRef, e: { nativeEvent?: MouseEvent }) => {
      if (props.selectionLocked) return;
      const ev = e.nativeEvent;
      const next = applyModuleClick(effectiveSelection, ref, selectionMode, {
        ctrl: ev?.ctrlKey,
        shift: ev?.shiftKey,
        meta: ev?.metaKey,
      });
      updateSelection(next);
      props.onModuleClick?.(ref);
    },
    [
      effectiveSelection,
      selectionMode,
      props.selectionLocked,
      props.onModuleClick,
      updateSelection,
    ],
  );

  const handleMoverClick = useMemo(
    () => (ref: MoverRef, e: { nativeEvent?: MouseEvent }) => {
      if (props.selectionLocked) return;
      const ev = e.nativeEvent;
      const next = applyMoverClick(effectiveSelection, ref, selectionMode, {
        ctrl: ev?.ctrlKey,
        shift: ev?.shiftKey,
        meta: ev?.metaKey,
      });
      updateSelection(next);
      props.onMoverClick?.(ref);
    },
    [
      effectiveSelection,
      selectionMode,
      props.selectionLocked,
      props.onMoverClick,
      updateSelection,
    ],
  );

  const handlePointerMissed = useCallback(() => {
    if (props.selectionLocked) return;
    const next = applyEmptyClick(effectiveSelection, selectionMode);
    if (next !== effectiveSelection) {
      updateSelection(next);
    }
  }, [
    effectiveSelection,
    selectionMode,
    props.selectionLocked,
    updateSelection,
  ]);

  return {
    effectiveSelection,
    selectionMode,
    handleModuleClick,
    handleMoverClick,
    handlePointerMissed,
  };
}
